import { cloneElement, ReactElement, useEffect, useMemo, useState } from 'react';
import { Icon } from '@components/index';
import { cx } from '@utils/cx';
import { Dropdown } from 'antd';
import { Checkbox, Collapse, Select } from 'global';
import { useTranslation } from 'react-i18next';

import styles from './treeSelect.module.scss';

interface Option {
  label: string;
  value?: any;
  children?: Option[];
}

interface IProps {
  status?: '' | 'error' | 'warning';
  value?: string[][];
  onChange?: (value: string[][]) => void;
  options: Option[];
  all?: boolean;
  children?: ReactElement;
}

const TreeSelect = ({ options: data, onChange, value, status, all, children }: IProps) => {
  const classNames = {
    all: 'cursor-pointer flex items-center justify-between border-[0] border-b-[1px] border-solid border-gray-200 px-[16px] py-[10px] text-[14px] font-[600] leading-[20px] text-[#344054]',
    collapse:
      ' px-[4px] [&_.ant-collapse-content-box]:!py-0 [&_.ant-collapse-expand-icon]:flex [&_.ant-collapse-expand-icon]:self-center [&_.ant-collapse-header]:items-center max-h-[350px] overflow-y-scroll [&_.ant-collapse-header]:!pr-[11px] [&_.ant-collapse-header]:!py-[7px] [&_.ant-collapse-header]:!pl-[7px] [&_.ant-collapse-header]:my-[4px] [&_.ant-collapse-header]:rounded-[8px] [&_.ant-collapse-content-box]:!px-[0] ',
    header:
      'text-gray-700 text-[14px] leading-[24px] font-[600] whitespace-nowrap text-ellipsis overflow-hidden max-w-[150px]',
    label:
      'rounded-[8px] pl-[48px] w-full flex justify-between items-center py-[8px] cursor-pointer pr-[11px] hover:bg-gray-100',
    container: cx('rounded-[12px] border-[1px] border-solid border-gray-100 bg-white overflow-hidden', styles.container)
  };
  const { t } = useTranslation();
  const [val, setVal] = useState<string[]>([]);

  const options = useMemo(() => {
    const convert = (data: Option[]) =>
      data?.reduce<Option[]>((p, c) => {
        if (c?.children) {
          p = [...p, { ...c, value: String(c?.value), children: undefined }, ...convert(c?.children)];
        } else {
          p = [...p, { ...c, value: String(c?.value) }];
        }

        return p;
      }, []);

    return convert(data);
  }, [data]);

  const childrenRecursiveValues = (children: Option[], parnetValue: string) =>
    (children || [])?.reduce<string[]>((p, c) => {
      if (c?.children) {
        p = [...p, ...childrenRecursiveValues(c?.children, `${parnetValue}-${c?.value}`)];
      } else {
        p = [...p, `${parnetValue}-${c?.value}`];
      }

      return p;
    }, []);

  const valuesFilter = (data: Option[]) =>
    data?.reduce<Record<string, string[]>>((p, c) => {
      if (c?.children) {
        p[String(c?.value)] = c?.children?.map(item => String(item?.value));
        p = {
          ...p,
          ...valuesFilter(c?.children)
        };
      }

      return p;
    }, {});

  const dataObj = useMemo(() => valuesFilter(data), [data]);

  const myValues = (val: string[]) => {
    const values = val?.reduce<string[][]>((p, c) => {
      const splitting = c?.split('-').slice(1);

      splitting?.forEach((v, i) => {
        if (!(p[i] || [])?.includes(v)) {
          p[i] = [...(p[i] || []), v];
        }
      });

      return p;
    }, []);

    const valuesRevers = values?.reverse();

    const transformedArray = valuesRevers?.map((v, i) => {
      const parents = valuesRevers[i + 1]?.map(p =>
        dataObj?.[p]?.every(v2 => v?.includes(v2)) ? [] : dataObj?.[p]?.filter(v2 => v?.includes(v2))
      );

      return valuesRevers[i + 1] ? parents?.flat(1) : v;
    });

    return transformedArray.reverse();
  };

  const changeOne = ({ value }: { value: any }) => {
    let valuesCopy = [...(val || [])];

    if (val?.includes(value)) {
      valuesCopy = valuesCopy?.filter(v => v !== value);
    } else {
      valuesCopy = [...valuesCopy, value];
    }

    setVal(valuesCopy);

    if (onChange) {
      onChange(myValues(valuesCopy));
    }
  };

  const parentChange = ({ checked, values }: { values: any[]; checked: boolean }) => {
    let valuesCopy = [...(val || [])];

    if (checked) {
      valuesCopy = valuesCopy?.filter(v => !values?.includes(v));
    } else {
      valuesCopy = [...valuesCopy, ...values];
    }

    setVal(valuesCopy);
    if (onChange) {
      onChange(myValues(valuesCopy));
    }
  };

  const allChange = (checked: boolean) => {
    let valuesCopy = [...(val || [])];

    if (checked) {
      valuesCopy = childrenRecursiveValues(data, '0');
    } else {
      valuesCopy = [];
    }

    setVal(valuesCopy);
    if (onChange) {
      onChange(myValues(valuesCopy));
    }
  };

  const isAll = () => childrenRecursiveValues(data, '0')?.every(v => val?.includes(v));
  const isIndeterminate = () => val?.length > 0 && !isAll();

  useEffect(() => {
    if (Array.isArray(value) && JSON.stringify(value) !== JSON.stringify(val)) {
      const valueChild = () => {
        const arr = value;

        const fc = (i: number, parent?: string): Option[] | undefined =>
          arr?.[i]
            ? (() => {
                const a = arr?.[i]
                  ?.map(item => ({
                    label: item,
                    value: item,
                    children: fc(i + 1, item)
                  }))
                  .filter(item => (parent ? dataObj?.[parent]?.includes(item?.value) : true));

                if (a?.length === 0 && parent) {
                  return dataObj?.[parent]?.map(item => ({
                    label: item,
                    value: item,
                    children: fc(i + 1, item)
                  }));
                }

                return a;
              })()
            : undefined;

        return childrenRecursiveValues(fc(0) || [], '0');
      };

      setVal(valueChild());
    }
  }, [value]);

  const CollapseRecursive = ({
    data,
    parentV,
    className
  }: {
    data: Option[] | undefined;
    parentV: string;
    className?: string;
  }) =>
    data?.some(v => v?.children) ? (
      <Collapse
        ghost
        expandIcon={({ isActive }) => <Icon name="arrowDawn" placement={isActive ? 'bottom' : 'right'} />}
        className={cx(classNames.collapse, className)}
      >
        {data?.map(parent => {
          const parentValue = `${parentV}-${parent?.value || 0}`;
          const childValues = childrenRecursiveValues(parent?.children || [], parentValue);
          const checked = childValues?.every(v => val?.includes(v));
          const indeterminate = childValues?.some(v => val?.includes(v)) && !checked;

          return (
            <Collapse.Panel
              forceRender
              header={
                <div title={parent?.label} className={classNames.header}>
                  {parent?.label}
                </div>
              }
              key={parentValue}
              extra={
                <Checkbox
                  indeterminate={indeterminate}
                  onClick={e => {
                    e.stopPropagation();
                  }}
                  onChange={() => parentChange({ values: childValues, checked })}
                  checked={checked}
                />
              }
            >
              {CollapseRecursive({
                data: parent?.children,
                parentV: parentValue,
                className: 'max-h-max overflow-auto'
              })}
            </Collapse.Panel>
          );
        })}
      </Collapse>
    ) : (
      data?.map(item => {
        const itemValue = `${parentV}-${item?.value}`;
        const checked = val?.includes(itemValue);

        return (
          <label className={classNames.label} key={itemValue}>
            <span className="max-w-[150px] overflow-hidden text-ellipsis whitespace-nowrap" title={item?.label}>
              {item?.label}
            </span>
            <Checkbox checked={checked} onChange={() => changeOne({ value: itemValue })} />
          </label>
        );
      })
    );

  return children ? (
    <Dropdown
      overlay={
        <div
          className={cx(
            classNames.container,
            'shadow-[0_3px_6px_-4px_rgba(0,0,0,.12),0_6px_16px_0_rgba(0,0,0,.08),0_9px_28px_8px_rgba(0,0,0,.05)]'
          )}
        >
          {all ? (
            <label className={classNames.all}>
              {t('home.Barchasi')}{' '}
              <Checkbox
                indeterminate={isIndeterminate()}
                checked={isAll()}
                onChange={({ target: { checked } }) => allChange(checked)}
              />
            </label>
          ) : null}
          {CollapseRecursive({ data, parentV: '0' })}
        </div>
      }
      trigger={['click']}
    >
      {cloneElement(children)}
    </Dropdown>
  ) : (
    <Select
      status={status}
      value={myValues(val).flat(1)}
      mode="multiple"
      options={options}
      onChange={setVal}
      placeholder={t('home.Tanlang')}
      showArrow
      maxTagCount={1}
      dropdownClassName="p-0 rounded-[8px]"
      className="[&_.ant-select-selection-item]:rounded-[6px] [&_.ant-select-selection-item]:border-none [&_.ant-select-selection-item]:!bg-gray-100"
      dropdownRender={() => (
        <div className={classNames.container}>
          {all ? (
            <label className={classNames.all}>
              {t('home.Barchasi')}{' '}
              <Checkbox
                indeterminate={isIndeterminate()}
                checked={isAll()}
                onChange={({ target: { checked } }) => allChange(checked)}
              />
            </label>
          ) : null}
          {CollapseRecursive({ data, parentV: '0' })}
        </div>
      )}
    />
  );
};

export default TreeSelect;
